import type { ModulationPlan } from '@tunasong/music-lib'
import { buildModulationStabs, type ModulationPhaseBoundaries } from '../player/modulation-sequencer.js'
import { useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Modulation Lab audition hook. Thin wrapper around `useStabScheduler` —
 * builds stabs from the modulation plan and exposes the phase boundaries so
 * UI surfaces can highlight the current phase during playback. No looping
 * (each tap of "Play" plays one full 12-measure modulation).
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §4
 */

const BPM = 100

export interface UseModulationAuditionProps {
  plan: ModulationPlan | null
}

export interface ModulationAudition {
  play: () => void
  stop: () => void
  isPlaying: boolean
  phaseBoundaries: ModulationPhaseBoundaries | null
}

export const useModulationAudition = (props: UseModulationAuditionProps): ModulationAudition => {
  const { plan } = props
  const result = plan ? buildModulationStabs({ plan, bpm: BPM }) : null
  const { play, stop, isPlaying } = useStabScheduler({
    buildStabs: () => (result ? result.stabs : []),
  })
  return { play, stop, isPlaying, phaseBoundaries: result ? result.phaseBoundaries : null }
}
